import { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { ADDRESS_STATES, STEPS, ADDRESS_SOURCES } from '../../common/constants'
import { useUserContext } from '../../auth/UserProvider'
import ConfirmationIconAlertModal from '../../ui/modals/confirmationIconAlert'
import Loader from '../../ui/views/Loader'
import ShippingSendUI from '../../ui/views/shippingSend'

import { HOME } from '../../routes'

import Step1Origin from './Step1Origin'
import Step2Destination from './Step2Destination'
import Step3Shipment from './Step3Shipment'
import Step4AdditServices from './Step4AdditServices'
import Step5Summary from './Step5Summary'
import SuccessfulShipmentModal from './SuccessfulShipmentModal'
import useShippingSend from './useShippingSend'
import StepCustoms from './StepCustoms'

const AbandonInterceptionPrompt = ({ shipmentWasCreated, currentStep }) => {
  const [targetLocation, setTargetLocation] = useState(null)

  const history = useHistory()

  useEffect(() => {
    history.block((targetLocation, action) => {
      if (currentStep === STEPS.FROM) {
        history.block(() => {})
        history.push(targetLocation)
      } else {
        if (shipmentWasCreated) return true
        if (shipmentWasCreated) return true
        if (action !== 'PUSH' && action !== 'POP') return true

        setTargetLocation(targetLocation)
        return false
      }
    })

    return () => {
      history.block(() => {})
    }
  }, [history, shipmentWasCreated, currentStep])

  if (targetLocation === null) return null
  return (
    <ConfirmationIconAlertModal
      onConfirm={() => {
        history.block(() => {})
        history.push(targetLocation)
      }}
      onCancel={() => {
        setTargetLocation(null)
      }}
    />
  )
}

const ShippingSend = () => {
  const history = useHistory()

  const [newShipmentCode, setNewShipmentCode] = useState(null)
  const [shipmentWasCreated, setShipmentWasCreated] = useState(false)

  const {
    isLoading: isLoadingUser,
    defaultCentreCode,
    auditData,
  } = useUserContext()

  const {
    state,
    activeStep,
    stepStates,
    handleSubmitSuccess,
    handleClickStep,
    handleSubmitCustomSuccess,
    handleSetDirty,
    handleCancelConfirmation,
    handleAcceptConfirmation,
    handleSubmit,
    handleUpdatePickupError,
    isSubmitting,
  } = useShippingSend(auditData)
  const handleAbandonForm = () => {
    history.push(HOME.linkTo())
  }

  const handleSubmitShipping = e => {
    e.preventDefault()

    const isPickupSlotValid = validatePickupTimeSlot()
    if (isPickupSlotValid) {
      handleSubmit().then(shippingCode => {
        setNewShipmentCode(shippingCode)
      })
    } else {
      handleUpdatePickupError({
        key: 'shipping-send-form:error:time-slot',
      })
    }
  }

  const validatePickupTimeSlot = () => {
    // TODO improve pick up time slot validation by checking if the selected
    // time slot is still in time.
    return true
  }

  const determineIsPudo = addressState => {
    return addressState === ADDRESS_STATES.AT_PICKUP_POINT
  }
  const determineOriginPudo = addressSource => {
    return addressSource === ADDRESS_SOURCES.pickUpPoint
  }

  const determineIsInternational = country => {
    const internationalCountries = ['ES', 'PT']
    return !internationalCountries.includes(country?.code || country)
  }

  // Scroll to top on step changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [state.currentStep])

  if (isLoadingUser || state === null) {
    return <Loader />
  }

  const cutoffErrors = state.fieldErrors.from?.pickUpTimeSlot
  const selectedCentreCode =
    state.values?.from?.imputationCenter?.id ?? defaultCentreCode
  return (
    <ShippingSendUI
      hasCustom={state?.values?.shipment?.hasCustom}
      states={stepStates}
      currentStep={state.currentStep}
      onClickStep={handleClickStep}
    >
      <AbandonInterceptionPrompt
        currentStep={state.currentStep}
        shipmentWasCreated={shipmentWasCreated}
      />
      {newShipmentCode !== null ? (
        <SuccessfulShipmentModal
          isOpen={newShipmentCode !== null}
          onCloseClick={() => {
            setNewShipmentCode(null)
            setShipmentWasCreated(true)
          }}
          shippingCode={newShipmentCode}
          onNewShippingClick={() => {
            window.location.reload()
          }}
          onGoBackHome={() => {
            window.location.href = '/'
          }}
          isOriginPudo={determineOriginPudo(state.values.from?.addressSource)}
        />
      ) : null}

      {state.currentStep === STEPS.FROM ? (
        <Step1Origin
          savedValues={state.values[STEPS.FROM]}
          activeStep={activeStep}
          serverFieldErrors={state.fieldErrors[STEPS.FROM]}
          hasOtherError={
            !!state.fieldErrors[STEPS.TO] ||
            !!state.fieldErrors[STEPS.SHIPMENT] ||
            !!state.fieldErrors[STEPS.SERVICES]
          }
          onSubmitSuccess={handleSubmitSuccess}
          onCancel={handleAbandonForm}
        />
      ) : state.currentStep === STEPS.TO ? (
        <Step2Destination
          originCentreId={state.values.from.imputationCenter.id}
          centreCode={state.values.from.imputationCenter.id}
          originSource={state.values.from.addressSource}
          savedValues={state.values[STEPS.TO]}
          activeStep={activeStep}
          serverFieldErrors={state.fieldErrors[STEPS.TO]}
          hasOtherError={
            !!state.fieldErrors[STEPS.FROM] ||
            !!state.fieldErrors[STEPS.SHIPMENT] ||
            !!state.fieldErrors[STEPS.SERVICES]
          }
          onSubmitSuccess={handleSubmitSuccess}
          onCancel={handleAbandonForm}
          onSetDirty={handleSetDirty}
          hasConfirmation={state.confirmationAction !== null}
          onCancelConfirmation={handleCancelConfirmation}
          onAcceptConfirmation={handleAcceptConfirmation}
          isPudo={determineOriginPudo(state.values.from?.addressSource)}
        />
      ) : state.currentStep === STEPS.SHIPMENT ? (
        <Step3Shipment
          originOrDestinAddressChanged={state.originOrDestinAddressChanged}
          originAddressChanged={state.originAddressChanged}
          centreCode={selectedCentreCode}
          imputationCenter={state.values.from.imputationCenter?.name}
          originPostalCode={
            state.values.from.addressSource === 'newAddress'
              ? state.values.from.address.postalCode
              : state.values.from.selectedAddress.shippingAddress.postalCode
          }
          originCountryCode={
            state.values.from.addressSource === 'newAddress'
              ? state.values.from.address.country.code ?? state.values.to?.address.country
              : state.values.from.selectedAddress.shippingAddress.country
          }
          destinPostalCode={state.values?.to.address.postalCode}
          destinCountryCode={
            state.values.to?.addressState === ADDRESS_STATES.NEW ||
            state.values.to?.addressState === ADDRESS_STATES.AT_PICKUP_POINT
              ? state.values.to?.address.country.code ?? state.values.to?.address.country
              : state.values.to.selectedAddress.shippingAddress.country
          }
          isPudo={
            determineIsPudo(state.values.to?.addressState) ||
            determineOriginPudo(state.values.from?.addressSource)
          }
          isOriginPudo={
            determineOriginPudo(state.values.from?.addressSource)
          }
          international={determineIsInternational(
            state.values.to?.address.country,
          )}
          savedValues={state.values[STEPS.SHIPMENT]}
          activeStep={activeStep}
          serverFieldErrors={state.fieldErrors[STEPS.SHIPMENT]}
          hasOtherError={
            !!state.fieldErrors[STEPS.FROM] ||
            !!state.fieldErrors[STEPS.TO] ||
            !!state.fieldErrors[STEPS.SERVICES]
          }
          onSubmitSuccess={handleSubmitSuccess}
          onCancel={handleAbandonForm}
          setCustom={handleSubmitCustomSuccess}
          onSetDirty={handleSetDirty}
          hasConfirmation={state.confirmationAction !== null}
          onCancelConfirmation={handleCancelConfirmation}
          onAcceptConfirmation={handleAcceptConfirmation}
        />
      ) : state.currentStep === STEPS.CUSTOMS ? (
        <StepCustoms
          hasCustom={state.values[STEPS.SHIPMENT]?.hasCustom?.custom}
          destinCountryCode={
            state.values[STEPS.TO]?.address?.country?.code ??
            state.values[STEPS.TO]?.address?.country
          }
          originCountryCode={
            state.values[STEPS.FROM]?.address?.country?.code ??
            state.values[STEPS.FROM]?.address?.country
          }
          allowsElectronicalTradeDocuments={
            state.values[STEPS.SHIPMENT]?.hasCustom
              ?.allowsElectronicalTradeDocuments
          }
          centreCode={selectedCentreCode}
          savedValues={state.values[STEPS.CUSTOMS]}
          activeStep={activeStep}
          serverFieldErrors={state.fieldErrors[STEPS.SHIPMENT]}
          hasOtherError={
            !!state.fieldErrors[STEPS.FROM] ||
            !!state.fieldErrors[STEPS.TO] ||
            !!state.fieldErrors[STEPS.SERVICES]
          }
          onSubmitSuccess={handleSubmitSuccess}
          onCancel={handleAbandonForm}
          onSetDirty={handleSetDirty}
          hasConfirmation={state.confirmationAction !== null}
          onCancelConfirmation={handleCancelConfirmation}
          onAcceptConfirmation={handleAcceptConfirmation}
        />
      ) : state.currentStep === STEPS.SERVICES ? (
        <Step4AdditServices
          international={determineIsInternational(
            state.values.to?.address.country,
          )}
          isPudo={
            determineIsPudo(state.values.to?.addressState) ||
            determineOriginPudo(state.values.from?.addressSource)
          }
          savedValues={state.values[STEPS.SERVICES]}
          activeStep={activeStep}
          serverFieldErrors={state.fieldErrors[STEPS.SERVICES]}
          hasOtherError={
            !!state.fieldErrors[STEPS.FROM] ||
            !!state.fieldErrors[STEPS.TO] ||
            !!state.fieldErrors[STEPS.SHIPMENT]
          }
          onSubmitSuccess={handleSubmitSuccess}
          onCancel={handleAbandonForm}
          onSetDirty={handleSetDirty}
          hasConfirmation={state.confirmationAction !== null}
          onCancelConfirmation={handleCancelConfirmation}
          onAcceptConfirmation={handleAcceptConfirmation}
        />
      ) : state.currentStep === STEPS.SUMMARY ? (
        <Step5Summary
          shipmentWasCreated={shipmentWasCreated}
          values={state.values}
          newShipmentCode={newShipmentCode}
          fieldErrors={state.fieldErrors}
          formErrors={state.formErrors}
          onCancel={handleAbandonForm}
          onSubmit={handleSubmitShipping}
          isSubmitting={isSubmitting}
          activeStep={activeStep}
          cutoffErrors={cutoffErrors}
          onClickStep={handleClickStep}
        />
      ) : null}
    </ShippingSendUI>
  )
}

export default ShippingSend
